import { useState, useEffect } from 'react';
import { FiSearch, FiFilter, FiPlus } from 'react-icons/fi';
import { postsAPI, categoriasAPI } from '../utils/api';
import PostCard from '../components/PostCard';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import storageService from '../services/storageService';
import syncService from '../services/syncService';

const Estudios = () => {
  const [posts, setPosts] = useState([]);
  const [postsLocales, setPostsLocales] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('');
  const [ordenar, setOrdenar] = useState('reciente');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    cargarCategorias();
    cargarPostsLocales();
    
    // Sincronización automática al montar
    if (user?.id) {
      syncService.syncPosts(user.id)
        .then(result => {
          if (result.syncedCount > 0) {
            console.log(`🎉 ${result.syncedCount} posts fueron aprobados y publicados`);
            // Recargar posts locales después de sincronizar
            cargarPostsLocales();
          }
        })
        .catch(error => {
          console.error('Error en sincronización automática:', error);
        });
    }
  }, [user?.id]);

  useEffect(() => {
    cargarPosts();
  }, [searchTerm, selectedCategoria, ordenar, page]);

  const cargarPostsLocales = async () => {
    if (!user) return;
    
    try {
      await storageService.init();
      const locals = await storageService.posts.obtenerTodos();
      console.log('📝 Posts locales cargados:', locals.length);
      setPostsLocales(locals);
    } catch (error) {
      console.error('Error cargando posts locales:', error);
    }
  };

  const cargarCategorias = async () => {
    try {
      const response = await categoriasAPI.listar();
      setCategorias(response.data.data);
    } catch (error) {
      console.error('Error al cargar categorías:', error);
    }
  };

  const cargarPosts = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 12,
        busqueda: searchTerm,
        ordenar
      };

      if (selectedCategoria) {
        params.categoria = selectedCategoria;
      }

      const response = await postsAPI.getPublicados(params);
      setPosts(response.data.data.posts || []);
      setTotalPages(response.data.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error al cargar posts:', error);
      setPosts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    cargarPosts();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold mb-4">Estudios Bíblicos</h1>
          <p className="text-indigo-100 text-lg">
            Explora artículos sobre teología, historia bíblica y estudios profundos de las Escrituras
          </p>
          
          {hasRole('administrador') && (
            <Link
              to="/admin/posts"
              className="inline-block mt-4 bg-white text-indigo-600 px-6 py-2 rounded-lg font-semibold hover:bg-indigo-50 transition"
            >
              Panel de Administración
            </Link>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Barra de búsqueda y filtros */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
            {/* Búsqueda */}
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar por título, descripción o palabras clave..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filtro por categoría */}
            <div className="md:w-64">
              <div className="relative">
                <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <select
                  value={selectedCategoria}
                  onChange={(e) => {
                    setSelectedCategoria(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none"
                >
                  <option value="">Todas las categorías</option>
                  {categorias.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nombre} ({cat.conteoPosts || 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Ordenar */}
            <div className="md:w-48">
              <select
                value={ordenar}
                onChange={(e) => {
                  setOrdenar(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="reciente">Más recientes</option>
                <option value="antiguos">Más antiguos</option>
                <option value="masVistos">Más vistos</option>
              </select>
            </div>
          </form>
        </div>

        {/* Posts locales del usuario (si tiene) */}
        {user && postsLocales.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              📝 Mis Posts Locales
              <span className="text-sm font-normal text-gray-500">({postsLocales.length})</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 p-4 bg-purple-50 rounded-lg">
              {postsLocales.map((post) => (
                <PostCard 
                  key={`local_${post.id}`} 
                  post={{
                    ...post,
                    slug: `local_${post.id}`,
                    esLocal: true
                  }} 
                  onDelete={() => cargarPostsLocales()}
                />
              ))}
            </div>
          </div>
        )}

        {/* Grid de posts publicados */}
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          📚 Posts Publicados
        </h2>
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No se encontraron estudios</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Anterior
                </button>
                
                <span className="text-gray-700">
                  Página {page} de {totalPages}
                </span>
                
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Botón flotante para crear post (solo usuarios registrados) */}
      {user && (
        <div className="fixed bottom-8 right-8 z-30">
          <button
            onClick={() => navigate('/posts/crear')}
            className="w-14 h-14 sm:w-16 sm:h-16 bg-indigo-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center"
            title="Crear nuevo post"
          >
            <FiPlus className="w-6 h-6 sm:w-8 sm:h-8" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Estudios;
